import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import ReactDOMServer from 'react-dom/server';
import { AppVarsContext } from '../../context/AppVarsContext';
import { applyApexBrandGradient } from '../../system/apex-gradient';
import { useToken } from '../../Login/TokenProvider';
import { reportColors, styleReportChart } from '../../views/shared/report-design';
import { formatUsd } from '../../system/report-formatters';

const NOTES_CACHE = new Map();
const NOTE_TYPE_LABELS = {
    general: 'General',
    google_paid: 'Google Paid',
    google_grant: 'Google Grant',
    meta: 'Meta',
    bing: 'Bing',
    display: 'Programmatic',
    stackadapt: 'Programmatic',
    email: 'Email',
    other: 'Other'
};
const CLIENT_VISIBLE_VIEW_ID = 'a7edd5c0-7a77-49ed-86ad-6fe3066a0f59';

class ApexChartBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
            this.setState({ hasError: false });
        }
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback || null;
        }

        return this.props.children;
    }
}

const TrendChart = ({
    trendData,
    notes,
    includeNotes = true,
    noteType = null,
    noteView = null,
    showCost = true,
    showRevenue = showCost,
    disableAnimation = false,
    reportStyle = false
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { appVars } = useContext(AppVarsContext);
    const tokenContext = useToken();
    const adminToken = Number(tokenContext?.token?.token || 0);
    const debugEnabled = typeof window !== 'undefined' && window?.localStorage?.getItem('debugTrendChart') === '1';
    const palette = theme?.palette || {};
    const primary = palette.primary || {};
    const secondary = palette.secondary || {};
    const safePrimaryMain = primary.main || '#1976d2';
    const safePrimaryLight = primary.light || safePrimaryMain;
    const safePrimaryDark = primary.dark || safePrimaryMain;
    const safeSecondaryMain = secondary.main || '#9c27b0';

    if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.debug('[TrendChart] theme palette:', { primary, secondary, divider: palette.divider, mode: palette.mode });
    }

    const rows = Array.isArray(trendData)
        ? trendData.filter((row) => row && typeof row === 'object')
        : [];

    const [notesData, setNotesData] = useState(() => (Array.isArray(notes) ? notes : []));
    const [notesRefreshKey, setNotesRefreshKey] = useState(0);

    useEffect(() => {
        if (Array.isArray(notes)) {
            setNotesData(notes);
        }
    }, [notes]);

    useEffect(() => {
        if (!includeNotes || Array.isArray(notes)) return;
        if (typeof window === 'undefined') return undefined;
        const handleRefresh = () => setNotesRefreshKey((prev) => prev + 1);
        window.addEventListener('dc', handleRefresh);
        return () => {
            window.removeEventListener('dc', handleRefresh);
        };
    }, [includeNotes, notes]);

    useEffect(() => {
        if (!includeNotes || Array.isArray(notes)) return;
        if (!appVars?.api_url) return;
        const cid = sessionStorage.getItem('rid');
        if (!cid) return;
        const sDate = sessionStorage.getItem('sDate');
        const eDate = sessionStorage.getItem('eDate');
    const resolvedView = noteView ?? CLIENT_VISIBLE_VIEW_ID;
        const cacheKey = `${cid}|${sDate || ''}|${eDate || ''}|${resolvedView || 'all'}`;
        if (NOTES_CACHE.has(cacheKey)) {
            setNotesData(NOTES_CACHE.get(cacheKey));
            return;
        }

        let isMounted = true;
        fetch(`${appVars.api_url}/comments/get`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                idi: cid,
                sdate: sDate,
                edate: eDate,
                view: resolvedView || 'all'
            })
        })
            .then(async (res) => {
                const data = await res.json().catch(() => []);
                if (!res.ok) {
                    throw new Error(data?.error || `comments/get ${res.status}`);
                }
                return Array.isArray(data) ? data : [];
            })
            .then((data) => {
                if (!isMounted) return;
                NOTES_CACHE.set(cacheKey, data);
                setNotesData(data);
            })
            .catch(() => {
                if (!isMounted) return;
                setNotesData([]);
            });

        return () => {
            isMounted = false;
        };
    }, [includeNotes, notes, appVars?.api_url, notesRefreshKey, noteView, adminToken]);

    // Case-insensitive key lookup with fallbacks
    const keyOf = (row, candidates) => {
        if (!row) return null;
        const keys = Object.keys(row);
        const lowerMap = new Map(keys.map(k => [k.toLowerCase(), k]));
        for (const c of candidates) {
            const hit = lowerMap.get(String(c).toLowerCase());
            if (hit) return hit;
        }
        return null;
    };

    const first = rows[0] || {};

    const dateKey = keyOf(first, ['date', 'day', 'dt', 'segments.date', 'DATE', 'DAY', 'x', 'X', 'label', 'LABEL']);
    const impressionsKey = keyOf(first, ['impressions', 'IMPR', 'IMPRESSIONS']);
    const clicksKey = keyOf(first, ['clicks', 'CLICKS']);
    const conversionsKey = keyOf(first, ['conversions', 'CONVERSIONS', 'conv', 'CONV']);
    const costKey = keyOf(first, ['cost', 'COST', 'spend', 'SPEND']);
    const revenueKey = keyOf(first, ['revenue', 'REVENUE']);

    const safeNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };
    const fmtNumber = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return '0';
        const hasDecimals = Math.abs(n % 1) > 0;
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: hasDecimals ? 2 : 0 }).format(n);
    };
    const fmtMoney = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return '$0.00';
        return formatUsd(n);
    };
    const parseDateLabel = (label) => {
        if (!label) return null;
        const text = String(label);
        const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            const year = Number(isoMatch[1]);
            const month = Number(isoMatch[2]);
            const day = Number(isoMatch[3]);
            const localDate = new Date(year, month - 1, day);
            if (!Number.isNaN(localDate.getTime())) return localDate;
        }
        const fallback = new Date(text);
        return Number.isNaN(fallback.getTime()) ? null : fallback;
    };
    const fmtDateLabel = (label) => {
        const date = parseDateLabel(label);
        return date ? date.toLocaleDateString() : label;
    };
    const stripHtml = (html) => {
        if (!html) return '';
        return String(html)
            .replace(/<style[\s\S]*?<\/style>/gi, '')
            .replace(/<script[\s\S]*?<\/script>/gi, '')
            .replace(/<\/?[^>]+(>|$)/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    };
    const sanitizeNoteHtml = (html) => {
        if (!html) return '';
        return String(html)
            .replace(/<style[\s\S]*?<\/style>/gi, '')
            .replace(/<script[\s\S]*?<\/script>/gi, '');
    };

    const normalizeDateKey = (raw) => {
        if (!raw) return '';
        const text = String(raw);
        const isoMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch) return isoMatch[1];
        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
        return text.slice(0, 10);
    };

    const notesByDate = useMemo(() => {
        if (!includeNotes) return {};
        const map = {};
        const normalizedType = noteType && noteType !== 'All' ? String(noteType) : null;
        (Array.isArray(notesData) ? notesData : []).forEach((note) => {
            const noteTypeValue = note?.comment?.type || note?.type || 'general';
            if (normalizedType && noteTypeValue !== normalizedType) return;
            const noteDate = normalizeDateKey(note?.comment?.date || note?.createdAt);
            if (!noteDate) return;
            if (!map[noteDate]) map[noteDate] = [];
            map[noteDate].push(note);
        });
        return map;
    }, [notesData, includeNotes, noteType]);

    const categories = rows.map((r) => {
        const raw = dateKey ? r?.[dateKey] : null;
        return normalizeDateKey(raw) || (raw != null ? String(raw) : '');
    });

    const availableCategorySet = useMemo(() => (
        new Set(categories.filter(Boolean))
    ), [categories]);

    const noteDates = useMemo(() => {
        if (!includeNotes) return [];
        return Object.keys(notesByDate || {}).filter((date) => Boolean(date) && availableCategorySet.has(date));
    }, [notesByDate, includeNotes, availableCategorySet]);

    const series = [];

    if (impressionsKey) series.push({ name: 'Impressions', data: rows.map(r => safeNum(r?.[impressionsKey])) });
    if (clicksKey) series.push({ name: 'Clicks', data: rows.map(r => safeNum(r?.[clicksKey])) });
    if (conversionsKey) series.push({ name: 'Conversions', data: rows.map(r => safeNum(r?.[conversionsKey])) });

    // Cost/Revenue are often on a different scale; put them on a 2nd y-axis if present
    const moneySeries = [];
    if (showCost && costKey) moneySeries.push({ name: 'Spend', data: rows.map(r => safeNum(r?.[costKey])) });
    if (showRevenue && revenueKey) moneySeries.push({ name: 'Revenue', data: rows.map(r => safeNum(r?.[revenueKey])) });

    const moneySeriesNames = new Set(moneySeries.map((s) => String(s?.name || '').toLowerCase()));
    const moneyAxisTitle = showCost && showRevenue ? 'Spend / Revenue' : showRevenue ? 'Revenue' : 'Spend';

    const options = applyApexBrandGradient({
        chart: {
            type: 'line',
            height: 340,
            toolbar: { show: true },
            zoom: { enabled: false }
        },
        stroke: { width: 2, curve: 'smooth' },
        dataLabels: { enabled: false },
        theme: {
            mode: palette.mode === 'dark' ? 'dark' : 'light'
        },
        colors: [
            `${safePrimaryMain}`,
            `${secondary.light || safeSecondaryMain}`,
            `${safePrimaryLight}`,
            `${safeSecondaryMain}`,
            `${safePrimaryDark}`,
        ],
        grid: { borderColor: palette?.divider || 'rgba(0,0,0,0.12)' },
        xaxis: {
            categories,
            labels: { rotate: -45 }
        },
        yaxis: moneySeries.length
            ? [
                {
                    title: { text: 'Volume' },
                    labels: {
                        formatter: (v) => {
                            const n = Number(v);
                            if (!Number.isFinite(n)) return String(v);
                            return n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${Math.round(n)}`;
                        }
                    }
                },
                {
                    opposite: true,
                    title: { text: moneyAxisTitle },
                    labels: {
                        formatter: (v) => {
                            const n = Number(v);
                            if (!Number.isFinite(n)) return String(v);
                            return formatUsd(n);
                        }
                    }
                }
            ]
            : [
                {
                    title: { text: 'Volume' },
                    labels: {
                        formatter: (v) => {
                            const n = Number(v);
                            if (!Number.isFinite(n)) return String(v);
                            return n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${Math.round(n)}`;
                        }
                    }
                }
            ],
        tooltip: {
            shared: true,
            intersect: false,
            custom: ({ dataPointIndex, w }) => {
                const label = w?.globals?.categoryLabels?.[dataPointIndex] || w?.globals?.labels?.[dataPointIndex] || '';
                const dateLabel = fmtDateLabel(label);

                const seriesData = w?.config?.series || [];
                const rows = seriesData
                    .map((s) => {
                        const value = s?.data?.[dataPointIndex];
                        if (value === undefined || value === null) return null;
                        const seriesName = String(s?.name || '').toLowerCase();
                        const isMoney = moneySeriesNames.has(seriesName) || /cost|revenue|spend/i.test(seriesName);
                        return {
                            name: s?.name || 'Series',
                            value: isMoney ? fmtMoney(value) : fmtNumber(value)
                        };
                    })
                    .filter(Boolean);

                const noteDateKey = normalizeDateKey(label);
                const notesForDate = includeNotes ? (notesByDate[noteDateKey] || []) : [];
                const visibleNotes = notesForDate.slice(0, 3);

                return ReactDOMServer.renderToString(
                    <div style={{ background: `${theme.palette.primary.main}88`, minWidth: 240,maxWidth: 500, padding: '10px 12px', color: theme.palette.text.primary, borderRadius: 4, border: `4px solid ${theme.palette.divider}` }}>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                            {dateLabel}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 4, columnGap: 12, fontSize: 12 }}>
                            {rows.map((row) => (
                                <React.Fragment key={row.name}>
                                    <div>{row.name}</div>
                                    <div style={{ fontWeight: 600 }}>{row.value}</div>
                                </React.Fragment>
                            ))}
                        </div>
                        {visibleNotes.length ? (
                            <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${theme.palette.divider}` }}>
                                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>Notes</div>
                                <div style={{ display: 'grid', gap: 6, fontSize: 11 }}>
                                    {visibleNotes.map((note, index) => {
                                        const noteTypeValue = note?.comment?.type || note?.type || 'general';
                                        const noteLabel = NOTE_TYPE_LABELS[noteTypeValue] || noteTypeValue;
                                        const noteHtml = sanitizeNoteHtml(note?.comment?.comment || '');
                                        const noteText = stripHtml(noteHtml) || 'Note';
                                        const noteTimeRaw = note?.comment?.date || note?.createdAt;
                                        const noteTime = noteTimeRaw ? new Date(noteTimeRaw) : null;
                                        const timeLabel = noteTime && !Number.isNaN(noteTime.getTime())
                                            ? noteTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                            : '';
                                        const viewValue = note?.comment?.view || note?.view;
                                        const visibilityLabel = viewValue === CLIENT_VISIBLE_VIEW_ID ? 'Client' : 'Internal';
                                        return (
                                            <div key={`${note?.pkRecID || index}`} style={{ display: 'grid', gap: 2 }}>
                                                <div style={{ fontWeight: 600 }}>
                                                    {noteLabel}{timeLabel ? ` · ${timeLabel}` : ''} · {visibilityLabel}
                                                </div>
                                                <div
                                                    style={{
                                                        color: theme.palette.text.secondary,
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                        overflowWrap: 'anywhere'
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: noteHtml || noteText }}
                                                />
                                            </div>
                                        );
                                    })}
                                    {notesForDate.length > visibleNotes.length ? (
                                        <div style={{ color: theme.palette.text.secondary }}>
                                            +{notesForDate.length - visibleNotes.length} more
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                    </div>
                );
            }
        },
        annotations: {
            xaxis: noteDates.map((date) => ({
                x: date,
                borderColor: theme.palette.warning.main,
                label: {
                    text: '●',
                    style: {
                        color: theme.palette.warning.main,
                        background: 'transparent',
                        fontSize: '10px',
                        fontWeight: 700
                    },
                    offsetY: 6
                }
            })),
            yaxis: [],
            points: [],
            images: []
        },
        legend: { position: 'right', show: !isMobile }
    }, theme, 'area', { opacityFrom: 0.34, opacityTo: 0.05 });

    // Merge series with a 2nd axis for money series
    const combinedSeries = moneySeries.length
        ? [
            ...series.map(s => ({ ...s, yAxisIndex: 0 })),
            ...moneySeries.map(s => ({ ...s, yAxisIndex: 1 }))
        ]
        : series;

    const reportSeriesColors = { Impressions: reportColors.categories[5], Clicks: reportColors.categories[6], Conversions: reportColors.conversions, Spend: reportColors.categories[2], Revenue: reportColors.revenue };
    const renderOptions = reportStyle ? styleReportChart(theme, {
        ...options, chart: { ...options.chart, type: 'line' },
        colors: combinedSeries.map((item) => reportSeriesColors[item.name]),
        stroke: { width: 3, curve: 'straight' },
        xaxis: { ...options.xaxis, labels: { rotate: 0 } }
    }) : options;

    const safeSeries = combinedSeries.map((s) => ({
        ...s,
        data: Array.isArray(s?.data) ? s.data : []
    }));
    const seriesWithData = safeSeries.filter((s) => s.data.length > 0);

    if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.debug('[TrendChart] series lengths:', safeSeries.map((s) => ({ name: s.name, len: s.data.length })));
    }

    // If we somehow found nothing, don’t render an empty chart
    if (!seriesWithData.length) {
        if (debugEnabled) {
            // eslint-disable-next-line no-console
            console.debug('[TrendChart] no series generated, returning null');
        }
        return null;
    }

    const maxLen = Math.max(...seriesWithData.map((s) => s.data.length));
    const safeCategories = Array.isArray(categories) ? categories.slice(0, maxLen) : [];
    while (safeCategories.length < maxLen) safeCategories.push('');
    options.xaxis.categories = safeCategories;
    const hasUsableCategories = safeCategories.some(Boolean);
    const shouldAnimate = !disableAnimation && maxLen > 1 && hasUsableCategories;
    options.chart.animations = {
        enabled: shouldAnimate,
        animateGradually: { enabled: shouldAnimate },
        dynamicAnimation: { enabled: shouldAnimate }
    };
    options.stroke.curve = maxLen > 2 ? 'smooth' : 'straight';

    if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.debug('[TrendChart] categories length:', safeCategories.length);
    }

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                p: 1,
                borderRadius: { xs: 0, sm: 2 },
                border: `1px solid ${theme.palette.divider}`,
                background: reportStyle ? theme.palette.background.paper : `${theme.palette.primary.main}22`
            }}
        >
            <Typography fontWeight={900} sx={{ mb: 1 }}>Trend</Typography>
            <ApexChartBoundary
                resetKey={`${safeCategories.join('|')}|${seriesWithData.map((seriesItem) => `${seriesItem.name}:${seriesItem.data.length}`).join('|')}`}
                fallback={(
                    <Box
                        sx={{
                            minHeight: 240,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: 'rgba(255,255,255,0.45)'
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Trend visualization unavailable for this data set.
                        </Typography>
                    </Box>
                )}
            >
                <ReactApexChart options={renderOptions} series={seriesWithData} type={reportStyle ? "line" : "area"} height={340} />
            </ApexChartBoundary>
        </Box>
    );
};

TrendChart.propTypes = {

};

export default TrendChart;
